'use client'
import { CustomButtonNew, CustomTooltip, HamburgerMenu } from '@/components'
import { Button, Flex } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { REDIRECT_AFTER_LOGIN_COOKIE } from '@partment/types'
import { setCookie } from 'cookies-next/client'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import partment_stay_logo from 'public/images/Partment_logo_from_property_details.svg'
import partment_stay_logo_white from 'public/images/Partment_Rental_logo_white.svg'
import { useEffect, useRef, useState } from 'react'
import { usePromoBannerStore } from '../store'
import { HamMenuIcon } from './HamMenuIcon'
import { onHandleLogout } from '@/utils/logout'
import { LuLogOut } from 'react-icons/lu'


// Sentinel ref for IntersectionObserver
const Navbar = ({ isLogin }) => {
  const [opened, { open, close }] = useDisclosure(false)
  const [isWhiteBg, setIsWhiteBg] = useState(false)
  const [mounted, setMounted] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [active, setActive] = useState('')
  const desktopMenuRef = useRef(null)
  const { bannerClosed } = usePromoBannerStore()
  const router = useRouter()

  // Sentinel ref for IntersectionObserver
  const sentinelRef = useRef(null)
  const [isMobile, setIsMobile] = useState(true) // Default to mobile for SSR

  // Check if we're on mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768) // md breakpoint
    }

    // Set initial value
    checkMobile()

    // Add resize listener
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Intersection Observer to toggle navbar bg - DESKTOP ONLY
  useEffect(() => {
    setMounted(true)

    // If mobile, don't set up intersection observer
    if (isMobile) {
      setIsWhiteBg(false) // Always transparent on mobile
      return
    }

    // Only set up intersection observer for desktop
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsWhiteBg(!entry.isIntersecting)
      },
      { threshold: [0] },
    )

    if (sentinelRef.current) {
      observer.observe(sentinelRef.current)
    }

    return () => {
      if (sentinelRef.current) {
        observer.unobserve(sentinelRef.current)
      }
    }
  }, [isMobile]) // Re-run when isMobile changes

  // Outside click for desktop menu
  useEffect(() => {
    function handleOutsideClick(e) {
      if (
        desktopMenuRef.current &&
        !desktopMenuRef.current.contains(e.target)
      ) {
        setActive('')
      }
    }
    document.addEventListener('mousedown', handleOutsideClick)
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
    }
  }, [])

  // Scroll-to-section handler
  const scrollToSection = (sectionId) => {
    setMobileMenuOpen(false)
    setTimeout(() => {
      const element = document.getElementById(sectionId)
      if (element) {
        const offsetTop =
          element.getBoundingClientRect().top + window.pageYOffset - 100
        window.scrollTo({
          top: offsetTop,
          behavior: 'smooth',
        })
      }
    }, 100)
  }

  // Determine navbar classes based on device and state
  const getNavbarClasses = () => {
    // Mobile always has transparent background
    if (isMobile) {
      return bannerClosed
        ? 'bg-transparent md:top-[65px] top-[5px] text-white mt-0 w-full !text-[14px]'
        : 'bg-transparent md:top-[65px] text-white w-full !text-[14px] font-medium'
    }

    // Desktop with white background
    if (isWhiteBg) {
      return 'bg-white top-0 shadow-md text-[#191919] md:w-[919px] z-50 mx-auto gap-5 font-medium rounded-[18px] h-[66px] !text-[15px] flex items-center justify-center left-1/2 -translate-x-1/2'
    }

    // Desktop with transparent background
    return bannerClosed
      ? 'bg-transparent md:top-[65px] top-[5px] text-white mt-0 w-full !text-[14px]'
      : 'bg-transparent md:top-[65px] text-white w-full !text-[14px] font-medium'
  }

  // Determine container classes
  const getContainerClasses = () => {
    if (isMobile || !isWhiteBg) {
      return `px-[26px] md:px-[55px] mx-auto max-[700px]:px-3 max-[1450px]:px-34 max-[1400px]:px-30 max-[1300px]:px-28 pt-[30px] md:pt-0 flex items-center justify-between`
    }
    return 'px-[26px] md:px-[55px] w-full px-6 md:px-8 h-full flex items-center justify-between gap-8'
  }

  // Determine logo and styles
  const getLogoDetails = () => {
    if (isMobile || !isWhiteBg) {
      return {
        src: partment_stay_logo_white,
        width: 100,
        height: 30,
        className: 'md:top-[-1px]',
      }
    }
    return {
      src: partment_stay_logo,
      width: 87,
      height: 28,
      className: 'md:top-[3px]',
    }
  }

  const logoDetails = getLogoDetails()

  function redirectToLogin() {
    const pathname = `${window.location.pathname}?${(() => {
      const p = new URLSearchParams(window.location.search)
      p.set('source', 'stays')
      return p.toString()
    })()}`

    setCookie(REDIRECT_AFTER_LOGIN_COOKIE, pathname, {
      path: '/',
      secure: process.env.NODE_ENV === 'production',
      sameSite: 'lax',
    })
  }

  return (
    <>
      {/* Sentinel element - ONLY for desktop */}
      {!isMobile && (
        <div
          ref={sentinelRef}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            height: 1,
            width: '100%',
          }}
          aria-hidden
        />
      )}

      <nav
        className={`fixed max-md:absolute z-40 left-0 overflow-x-hidden ${getNavbarClasses()}`}
      >
        <div className={getContainerClasses()}>
          {/* Logo */}
          <Flex>
            <Link
              href='/'
              className={!isMobile && isWhiteBg ? 'flex items-center' : ''}
            >
              <Image
                src={logoDetails.src}
                alt='Partment Stay Logo'
                width={logoDetails.width}
                height={logoDetails.height}
                priority
                style={{ height: 'auto' }}
                className={`object-contain mr-[30px] ${logoDetails.className}`}
              />
            </Link>
            <div
              ref={desktopMenuRef} // ⬅️ attach ref here
              className='hidden md:flex items-center gap-3'
            >
              <button
                onClick={() => {
                  setActive('testimonials')
                  scrollToSection('testimonials')
                }}
                aria-label="Scroll to testimonials section"
                className={`cursor-pointer font-medium rounded-md transition`}
              >
                Testimonials
              </button>

              <Link
                href='/list-your-stay'
                onClick={() => setActive('list')}
                className={`cursor-pointer rounded-[7px] font-medium transition-none h-[32px] py-1 px-3 gap-[10px] flex items-center justify-center`}
              >
                <span
                  className={`font-medium leading-[24px] tracking-normal`}
                >
                  List Your Property
                </span>
              </Link>
            </div>
          </Flex>

          {/* Sign-in & Headset (desktop only) */}
          <div className='hidden md:flex items-center gap-2.5'>
            {mounted && (
              <>
                {isLogin ? (
                  <>
                    <Button
                      component='a'
                      href='/global-portal'
                      radius='sm'
                      c='#000'
                      bg='#fff'
                      size='xs'
                    >
                      Dashboard
                    </Button>
                    <Button
                      component='a'
                      href='/stays/my-booking'
                      radius='sm'
                      c='#000'
                      bg='#fff'
                      size='xs'
                    >
                      My Bookings
                    </Button>
                    <CustomTooltip position='bottom' content={'Log out'}>
                      <CustomButtonNew
                        onClick={onHandleLogout}
                        ariaLabel='profile button'
                        className='p-3 text-lg font-semibold text-white bg-transparent border-2 border-white rounded-full md:text-xl hover:bg-white hover:text-azure_blue'
                        label={<LuLogOut />}
                      />
                    </CustomTooltip>
                  </>
                ) : (
                  <>
                    <Button
                      component='a'
                      href='/sso/login'
                      radius='sm'
                      c='#000'
                      bg='#fff'
                      size='xs'
                      onClick={redirectToLogin}
                    >
                      Login
                    </Button>
                    <Button
                      component='a'
                      href='/sso/signup'
                      radius='sm'
                      c='#000'
                      bg='#fff'
                      size='xs'
                      onClick={redirectToLogin}
                    >
                      Register
                    </Button>
                  </>
                )}
              </>
            )}
            {/* <CurrencySelector
              className={`${!isMobile && isWhiteBg ? 'font-medium text-[15px]' : ''}`}
            /> */}

            {/* <Link
              target='_blank'
              href={whatsappUrl}
              aria-label='Chat on WhatsApp'
              className={`bg-[#FFFFFF33] p-2.5 rounded-lg cursor-pointer inline-flex items-center justify-center ${
                !isMobile && isWhiteBg ? 'border border-black' : ''
              }`}
            >
              <MdOutlineHeadsetMic className='text-lg font-bold' />
            </Link> */}
          </div>

          {/* Mobile menu */}
          <div className='flex gap-2 md:hidden items-center'>
            {mounted && !isLogin && (
              <>
                <Button
                  component='a'
                  href='/sso/login'
                  radius='sm'
                  c='#000'
                  bg='#fff'
                  size='xs'
                  onClick={redirectToLogin}
                >
                  Login
                </Button>
                <Button
                  component='a'
                  href='/sso/signup'
                  radius='sm'
                  c='#000'
                  bg='#fff'
                  size='xs'
                  onClick={redirectToLogin}
                >
                  Register
                </Button>
              </>
            )}
            <Button size='xs' bg='#fff' onClick={open} aria-label='Open navigation menu'>
              {/* <Button size='xs' bg='#fff' onClick={handleToggleNav}> */}
              <span className='text-black text-xl'>
                <HamMenuIcon />
              </span>
            </Button>
            {mounted && isLogin && (
              <CustomTooltip position='bottom' content={'Log out'}>
                <CustomButtonNew
                  onClick={onHandleLogout}
                  ariaLabel='profile button'
                  className='p-3 text-lg font-semibold text-white bg-transparent border-2 border-white rounded-full md:text-xl hover:bg-white hover:text-azure_blue'
                  label={<LuLogOut />}
                />
              </CustomTooltip>
            )}
          </div>
        </div>
        <HamburgerMenu
          close={close}
          opened={opened}
          scrollToSection={scrollToSection}
          mounted={mounted}
          isLogin={isLogin}
        />
      </nav>
    </>
  )
}

// Button used for MobileMenu trigger
const Btn = ({ children, className = '', onClick }) => {
  return (
    <button
      type='button'
      onClick={onClick}
      className={`bg-white text-black md:px-5 text-sm font-medium rounded-lg cursor-pointer ${className}`}
    >
      {children}
    </button>
  )
}

export default Navbar
